import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useOutletContext,
} from "react-router-dom";

import Layout from "@/components/Layout";
import { tokens } from "@/lib/api";
import type { Me } from "@/lib/types";
import AcceptInvite from "@/pages/AcceptInvite";
import Agenda from "@/pages/Agenda";
import Customers from "@/pages/Customers";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import MyAgenda from "@/pages/MyAgenda";
import MyProfile from "@/pages/MyProfile";
import NewPassword from "@/pages/NewPassword";
import Onboarding from "@/pages/Onboarding";
import Reports from "@/pages/Reports";
import Services from "@/pages/Services";
import Settings from "@/pages/Settings";
import Signup from "@/pages/Signup";
import SiteSettings from "@/pages/SiteSettings";
import PainelLocale from "@/components/PainelLocale";
import Subscription from "@/pages/Subscription";
import Team from "@/pages/Team";
import Vehicles from "@/pages/Vehicles";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: true, // a agenda muda enquanto a aba fica aberta
      staleTime: 30_000,
    },
  },
});

function RequireAuth({ children }: { children: React.ReactNode }) {
  if (!tokens.access) return <Navigate to="/entrar" replace />;
  return <>{children}</>;
}

/** A home depende do papel: o profissional abre o app para ver a agenda
 *  dele, o dono para ver o resumo da casa. */
function RoleHome() {
  const me = useOutletContext<Me>();
  return me.role === "professional" ? <MyAgenda /> : <Home />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Sem basename: os ASSETS ficam em /static/app/ (vite `base`), mas as
          ROTAS ficam na raiz do host app.garagem.club. Usar BASE_URL
          aqui faz o Router recusar "/" e a tela fica branca. */}
      <Router>
        <Routes>
          <Route path="/entrar" element={<Login />} />
          <Route path="/criar-conta" element={<Signup />} />
          {/* Aberta: quem clica no convite ainda não tem conta nenhuma. */}
          <Route path="/convite/:token" element={<AcceptInvite />} />
          <Route
            path="/nova-senha"
            element={
              <RequireAuth>
                <NewPassword />
              </RequireAuth>
            }
          />
          {/* Fora do Layout de propósito: o painel depende do /me/, que
              devolve 402 com a conta suspensa — quem mais precisa pagar
              não conseguiria abrir a tela de pagamento lá dentro. */}
          <Route
            path="/assinatura"
            element={
              <RequireAuth>
                <PainelLocale>
                  <Subscription />
                </PainelLocale>
              </RequireAuth>
            }
          />
          <Route
            path="/comecar"
            element={
              <RequireAuth>
                <PainelLocale>
                  <Onboarding />
                </PainelLocale>
              </RequireAuth>
            }
          />
          <Route
            element={
              <RequireAuth>
                <Layout />
              </RequireAuth>
            }
          >
            <Route path="/" element={<RoleHome />} />
            <Route path="/agenda" element={<Agenda />} />
            <Route path="/clientes" element={<Customers />} />
            <Route path="/veiculos" element={<Vehicles />} />
            <Route path="/relatorios" element={<Reports />} />
            <Route path="/servicos" element={<Services />} />
            <Route path="/equipe" element={<Team />} />
            <Route path="/meu-perfil" element={<MyProfile />} />
            <Route path="/meu-site" element={<SiteSettings />} />
            <Route path="/configuracoes" element={<Settings />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </QueryClientProvider>
  );
}
